import { useCallback, useEffect, useRef, useState } from "react";
import { playerService } from "../../services/api";

// Compare page — data orchestration for 2–4 players.
//
// Per player three existing endpoints (no compare endpoint exists, and none is
// needed): GET /dashboard/players/public/{id}/ (bio/header),
// …/transfers/ and …/stats/. All three run through Promise.allSettled so each
// player's failures stay independent: a broken profile still leaves the search
// card data usable, a 404 on /stats/ simply means "no statistics" (the
// endpoint answers 404 with an empty result list), and transfers degrade to
// an empty history.
//
// Records are cached per selected player id, so adding a third player never
// refetches the first two. Removing a player (or Reset) PRUNES its record —
// re-adding the player therefore always triggers a fresh fetch, so a failed
// load is never served from cache as if it had succeeded. A record whose
// profile fetch failed keeps its `profileError`; the page renders an honest
// error state for that column and `reload` retries in place.

const sortTransfers = (list) =>
  [...list].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

export const useCompareData = (players) => {
  // id -> { loading, profile, profileError, transfers, stats }
  const [records, setRecords] = useState({});
  const inFlight = useRef(new Set());
  const wantedIds = useRef(new Set());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (id) => {
    if (id === null || id === undefined || inFlight.current.has(id)) return;
    inFlight.current.add(id);
    setRecords((r) => ({
      ...r,
      [id]: { loading: true, profile: null, profileError: null, transfers: [], stats: [] },
    }));

    const [profileRes, transfersRes, statsRes] = await Promise.allSettled([
      playerService.getPublicPlayerProfile(id),
      playerService.getPlayerTransfers(id),
      playerService.getPlayerStats(id),
    ]);

    inFlight.current.delete(id);
    if (!alive.current) return;
    // The player was removed while the fetch ran — don't resurrect a record
    // the prune below already dropped.
    if (!wantedIds.current.has(String(id))) return;

    const profile =
      profileRes.status === "fulfilled" && profileRes.value?.success
        ? profileRes.value.data
        : null;
    const profileError = profile
      ? null
      : (profileRes.status === "fulfilled" && profileRes.value?.error) ||
        "Failed to load player";

    let transfers = [];
    if (
      transfersRes.status === "fulfilled" &&
      transfersRes.value?.success &&
      Array.isArray(transfersRes.value.data)
    ) {
      transfers = sortTransfers(transfersRes.value.data);
    }

    // /stats/ 404s when a player has no statistics at all — that is a valid
    // empty state, not an error.
    let stats = [];
    if (statsRes.status === "fulfilled" && statsRes.value?.success) {
      const data = statsRes.value.data;
      if (Array.isArray(data)) stats = data;
      else if (Array.isArray(data?.results)) stats = data.results;
    }

    setRecords((r) => ({
      ...r,
      [id]: { loading: false, profile, profileError, transfers, stats },
    }));
  }, []);

  useEffect(() => {
    const list = (players || []).filter((p) => p?.id !== null && p?.id !== undefined);
    const ids = new Set(list.map((p) => String(p.id)));
    wantedIds.current = ids;

    // Prune records of removed players (Remove/Reset), so re-adding one
    // always starts a fresh fetch instead of replaying a stale failure.
    setRecords((r) => {
      const staleKeys = Object.keys(r).filter((k) => !ids.has(k));
      if (!staleKeys.length) return r;
      const next = { ...r };
      staleKeys.forEach((k) => {
        delete next[k];
      });
      return next;
    });

    list.forEach((p) => {
      if (!records[p.id]) load(p.id);
    });
  }, [players, records, load]);

  return { records, reload: load };
};

export default useCompareData;
